import { StreamId } from "@common/domain";
import {
  createEntityAdapter,
  EntityAdapter,
  EntityState,
  Update
} from "@ngrx/entity";
import { Action, createReducer, on } from "@ngrx/store";
import {
  RoomStreamToSubscribeVORaw,
  RoomStreamVORaw
} from "@youm-front/room/domain";
import { RoomActions, RoomStreamActions } from "../actions";
import { RoomEvents, RoomStreamEvents } from "../events";

export const ROOM_REMOTE_STREAMS_FEATURE_KEY = "roomRemoteStreams";

export interface RoomRemoteStreamsState extends EntityState<RoomStreamVORaw> {}

export const roomRemoteStreamsAdapter: EntityAdapter<RoomStreamVORaw> =
  createEntityAdapter<RoomStreamVORaw>({
    selectId: (_) => _.streamId
  });

const initialState: RoomRemoteStreamsState =
  roomRemoteStreamsAdapter.getInitialState();

// The stream fields shown by the room, the subscribe credentials excluded:
// they are fresh in every list, the subscription keeps its own.
const ROOM_STREAM_FIELDS = [
  "accountId",
  "accountIdentity",
  "roomId",
  "roomContextId",
  "streamIsActive",
  "streamKinds"
] as const;

function roomStreamChanges(
  current: RoomStreamVORaw,
  listed: RoomStreamToSubscribeVORaw
): Partial<RoomStreamVORaw> | null {
  const changed = ROOM_STREAM_FIELDS.filter(
    (field) => JSON.stringify(current[field]) !== JSON.stringify(listed[field])
  );

  return changed.length
    ? Object.fromEntries(changed.map((field) => [field, listed[field]]))
    : null;
}

/**
 * Converges the remote room streams to `streams`, the room stream list the
 * server returned for this client (a guest only gets the active streams):
 * - a listed stream missing from the store is added (missed ROOM_STREAM);
 * - a stored stream that is not listed is removed (missed delete);
 * - a stored stream whose fields differ is updated (missed update);
 * - a stream in `skipStreamIds` is left alone (newer event than the list).
 * Returns the same state when nothing differs (no selector churn).
 */
export function reconcileRoomRemoteStreams(
  state: RoomRemoteStreamsState,
  streams: RoomStreamToSubscribeVORaw[],
  skipStreamIds: StreamId[]
): RoomRemoteStreamsState {
  const skip = new Set(skipStreamIds);
  const listed = new Map(
    streams
      .filter((_) => !skip.has(_.streamId))
      .map((_) => [_.streamId, _] as const)
  );

  const toRemove = (state.ids as StreamId[]).filter(
    (id) => !skip.has(id) && !listed.has(id)
  );
  const toAdd: RoomStreamToSubscribeVORaw[] = [];
  const toUpdate: Update<RoomStreamVORaw>[] = [];
  for (const [id, stream] of listed) {
    const current = state.entities[id];
    const changes = current ? roomStreamChanges(current, stream) : null;
    if (!current) {
      toAdd.push(stream);
    } else if (changes) {
      toUpdate.push({ id, changes });
    }
  }

  if (!toRemove.length && !toAdd.length && !toUpdate.length) {
    return state;
  }

  return roomRemoteStreamsAdapter.addMany(
    toAdd,
    roomRemoteStreamsAdapter.updateMany(
      toUpdate,
      roomRemoteStreamsAdapter.removeMany(toRemove, state)
    )
  );
}

const reducer = createReducer(
  initialState,
  on(
    RoomActions.init,
    RoomActions.destroy,
    RoomActions.reset,
    () => initialState
  ),
  on(RoomEvents.roomStreamUpdate, (state, action) =>
    // Update only if exists in this store.
    // Don't use upsertOne, may be duplication with local streams
    roomRemoteStreamsAdapter.updateOne(
      {
        id: action.roomStreamUpdateEventVO.value.streamId,
        changes: action.roomStreamUpdateEventVO.value
      },
      state
    )
  ),
  on(RoomEvents.remoteRoomStream, (state, action) =>
    roomRemoteStreamsAdapter.upsertOne(
      action.roomStreamToSubscribeVO.value,
      state
    )
  ),
  on(
    RoomStreamActions.roomStreamDeleteSuccess,
    RoomStreamEvents.roomStreamDeleteEvent,
    (state, action) =>
      roomRemoteStreamsAdapter.removeOne(
        action.roomStreamDeleteCmd.value.streamId,
        state
      )
  ),
  on(RoomEvents.remoteRoomStreamsReconcile, (state, action) =>
    reconcileRoomRemoteStreams(state, action.streams, action.skipStreamIds)
  ),
  on(RoomActions.disposeRoomRemoteData, (state) =>
    roomRemoteStreamsAdapter.removeAll(state)
  )
);

export function roomRemoteStreamsReducer(
  state: RoomRemoteStreamsState | undefined,
  action: Action
) {
  return reducer(state, action);
}
